"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";

/**
 * Theme of the section currently sitting behind the navbar — so the bar can
 * flip its foreground to stay legible (black over light sections, white over
 * dark ones). Logic recovered from the old ScaleNav/useSectionTheme:
 *   • the probe line is the live nav bar's vertical centre (correct as the bar
 *     height changes), and
 *   • the section whose box straddles that line wins.
 * Sections opt in with `data-nav-theme="light" | "dark"` (works with server
 * components — just an attribute). Returns null when nothing tagged is behind
 * the bar; the navbar then keeps its default (dark) styling.
 */
export function useActiveSectionTheme(navSelector = ".rubyn-nav"): "light" | "dark" | null {
  const [theme, setTheme] = useState<"light" | "dark" | null>(null);
  // Re-run on client-side navigation: the new route renders different
  // [data-nav-theme] sections, so the bar must re-probe.
  const pathname = usePathname();

  useEffect(() => {
    let raf = 0;
    let nav: HTMLElement | null = null;

    const recompute = () => {
      raf = 0;
      if (!nav || !nav.isConnected) nav = document.querySelector<HTMLElement>(navSelector);
      let probeY = 32;
      if (nav) {
        const r = nav.getBoundingClientRect();
        probeY = (r.top + r.bottom) / 2;
      }
      let found: "light" | "dark" | null = null;
      for (const el of document.querySelectorAll<HTMLElement>("[data-nav-theme]")) {
        const r = el.getBoundingClientRect();
        if (r.top <= probeY && r.bottom > probeY) {
          const t = el.dataset.navTheme;
          if (t === "light" || t === "dark") found = t; // deepest match wins
        }
      }
      setTheme((prev) => (prev === found ? prev : found));
    };

    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(recompute);
    };

    recompute();
    // re-probe after the new route has painted (heights/images settled)
    const t = setTimeout(recompute, 60);
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    return () => {
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      clearTimeout(t);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [navSelector, pathname]);

  return theme;
}
